import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Card,
  Button,
  Typography,
  Space,
  message,
  Row,
  Col,
  Divider,
  Tag,
  Avatar,
  Modal,
  Form,
  DatePicker,
  Select,
  Alert,
  Popconfirm,
  Input,
  Switch,
  Collapse,
  Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  SwapOutlined,
  CalendarOutlined,
  PrinterOutlined,
  DeleteOutlined,
  UserOutlined,
  CarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  ExclamationCircleOutlined,
  ScanOutlined,
} from '@ant-design/icons';
import { Estimate } from '../types';
import api, { estimateApi } from '../services/api';
import { getAllUsers } from '../services/authApi';
import PDFConfigModal from './PDFConfigModal';
import SendEstimateModal from './Estimate/SendEstimateModal';
import SMSConversationThread from './SMS/SMSConversationThread';
import PartsCatalogButton from './PartsCatalogButton';
import { ActivityLogComponent } from './ActivityLog/ActivityLog';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { socketService } from '../services/socketService';
import { AvailabilityPicker } from './Appointments/AvailabilityPicker';
import { AppointmentModal, EnhancedAppointmentModal } from './Appointments';
import { QuickBookingDrawer } from './SchedulingHub/QuickBookingDrawer';
import { appointmentApi, AvailableTimeSlot } from '../services/appointmentApi';
import { assetLabel } from '../utils/assetLabel';
import { feesFromStored } from '../utils/estimateTotals';
import dayjs from 'dayjs';
import ClipButton from './Clip/ClipButton';
import VehicleQuickCapture from './Clip/VehicleQuickCapture';
import { useClip } from '../hooks/useClip';
import { useHasRole, useCapability } from './auth/RoleGuard';
import ServiceItemsTable from './ServiceItemsTable';
import ShopNotesPanel from './Shared/ShopNotesPanel';
import RepairPriorityCard from './Shared/RepairPriorityCard';

import LookupQueuePanel from './Shared/LookupQueuePanel';
import { dviApi } from '../services/api/dviApi';
import type { DVIInspectionFull } from '../types/dvi';
import { InspectionStatusCard } from './DVI/InspectionStatusCard';
import SendDialog from './Shared/SendDialog';
import { EstimateApprovalReview } from './Approval/EstimateApprovalReview';
import { useBreakpointDown } from '../constants/breakpoints';
import './EstimateDetail.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const EstimateDetail: React.FC = () => {
  // Bumped when a lookup is queued, to remount the queue panel so a job just
  // started shows immediately rather than on the next poll.
  const [queueSeq] = useState(0);
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [loading, setLoading] = useState(true);
  const [convertModalVisible, setConvertModalVisible] = useState(false);
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [pdfConfigModalVisible, setPdfConfigModalVisible] = useState(false);
  const [sendEstimateModalVisible, setSendEstimateModalVisible] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [convertForm] = Form.useForm();
  const [selectedAppointmentSlot, setSelectedAppointmentSlot] = useState<AvailableTimeSlot | null>(null);
  const [appointmentModalVisible, setAppointmentModalVisible] = useState(false);
  const [smartBookingModalVisible, setSmartBookingModalVisible] = useState(false);
  const [existingAppointment, setExistingAppointment] = useState<any>(null);
  const [rescheduleModalVisible, setRescheduleModalVisible] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState('');
  // undefined = not loaded or fetch failed (render nothing); null = confirmed no inspection
  const [dviInspection, setDviInspection] = useState<DVIInspectionFull | null | undefined>(undefined);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { subscribeToEntity, unsubscribeFromEntity } = useNotifications();
  const { user } = useAuth();

  const canApplyDiscounts = useCapability('apply_discounts');
  const isMobileView = useBreakpointDown('lg');
  // Web-clip mode (J-C) — feature-flagged by REACT_APP_WEB_CLIP=true
  const isOwnerOrAdvisor = useHasRole(['owner', 'advisor']);
  const estimateIdNum = id ? parseInt(id, 10) : 0;
  const clip = useClip('estimate', estimateIdNum);
  // WP-06 Part 2 — phone-native capture for year/make/model/VIN. Web clip needs a
  // desktop extension; this needs nothing but a keyboard.
  const [vehicleCaptureOpen, setVehicleCaptureOpen] = useState(false);

  // Edit guard: prevent WebSocket updates from resetting form state during editing
  const isEditingRef = useRef(false);
  const pendingUpdateRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Determine route prefix based on current path
  const getRoutePrefix = () => {
    if (location.pathname.startsWith('/owner')) return '/owner';
    if (location.pathname.startsWith('/advisor')) return '/advisor';
    if (location.pathname.startsWith('/technician')) return '/technician';
    if (location.pathname.startsWith('/portal')) return '/portal';
    return '/admin';
  };

  const routePrefix = getRoutePrefix();

  useEffect(() => {
    if (id) {
      setDviInspection(undefined); // reset so a stale inspection never shows while navigating between estimates
      fetchEstimate(parseInt(id));
      fetchExistingAppointment(parseInt(id));
      dviApi.getByEstimate(parseInt(id))
        .then(setDviInspection)
        .catch(() => setDviInspection(undefined));
    }
  }, [id]);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Real-time updates subscription
  useEffect(() => {
    if (!id) return;

    // Subscribe to this estimate for real-time updates
    subscribeToEntity('estimate', parseInt(id));
    console.log(`🔔 Subscribed to estimate ${id} for real-time updates`);

    // Listen for entity updates (guarded by editing state)
    const handleEntityUpdate = (data: any) => {
      console.log('🔄 Estimate updated in real-time:', data);

      // Guard: only act on updates relevant to THIS estimate. The server emits
      // entity_updated into the estimate room for direct estimate changes
      // (entityType 'estimate', entityId = this id) and for appointment
      // scheduling (entityType 'appointment', estimateId = this id).
      const thisId = parseInt(id);
      const isThisEstimate = data?.entityType === 'estimate' && data?.entityId === thisId;
      const isRelatedAppointment = data?.estimateId === thisId;
      if (!isThisEstimate && !isRelatedAppointment) {
        return;
      }

      if (isEditingRef.current) {
        // User is editing — queue the update instead of refetching
        if (pendingUpdateRef.current) clearTimeout(pendingUpdateRef.current);
        pendingUpdateRef.current = setTimeout(() => {
          if (!isEditingRef.current && id) {
            fetchEstimate(parseInt(id));
          }
        }, 2000);
        return;
      }

      // Not editing — refresh immediately
      if (id) {
        fetchEstimate(parseInt(id));
      }

      // Show a subtle notification
      message.info({
        content: 'Estimate updated',
        duration: 2,
        style: { marginTop: '60px' },
      });
    };

    socketService.on('entity_updated', handleEntityUpdate);

    // Cleanup on unmount
    return () => {
      unsubscribeFromEntity('estimate', parseInt(id));
      socketService.off('entity_updated', handleEntityUpdate);
      if (pendingUpdateRef.current) clearTimeout(pendingUpdateRef.current);
      console.log(`🔕 Unsubscribed from estimate ${id}`);
    };
  }, [id, subscribeToEntity, unsubscribeFromEntity]);

  const fetchEstimate = async (estimateId: number) => {
    try {
      setLoading(true);
      const response = await estimateApi.getById(estimateId);
      setEstimate(response.data);
    } catch (error) {
      console.error('Error fetching estimate:', error);
      message.error('Failed to fetch estimate details');
      navigate(`${routePrefix}/estimates`);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyPromo = async (code: string) => {
    if (!estimate?.id) return;
    setPromoBusy(true);
    try {
      await estimateApi.applyPromo(estimate.id, code);
      setPromoInput('');
      await fetchEstimate(estimate.id);
      message.success('Promo code applied');
    } catch (error: any) {
      if (error?.response?.status === 400) message.error('Invalid or expired promo code');
      else message.error('Failed to apply promo code');
    } finally {
      setPromoBusy(false);
    }
  };

  const handleRemovePromo = async () => {
    if (!estimate?.id) return;
    setPromoBusy(true);
    try {
      await estimateApi.removePromo(estimate.id);
      await fetchEstimate(estimate.id);
      message.success('Promo code removed');
    } catch {
      message.error('Failed to remove promo code');
    } finally {
      setPromoBusy(false);
    }
  };

  const fetchExistingAppointment = async (estimateId: number) => {
    try {
      const appointments = await appointmentApi.getAll({ estimate_id: estimateId });
      // Find non-cancelled appointment for this estimate
      const appointment = appointments.find(apt => apt.status !== 'cancelled');
      setExistingAppointment(appointment || null);
    } catch (error) {
      console.error('Error fetching appointment:', error);
      // Don't show error message - appointment might not exist
    }
  };

  const fetchUsers = async () => {
    try {
      // Axios helper → inherits the 401 token-refresh interceptor
      const data = await getAllUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status || 'draft') {
      case 'draft': return '#6b7280';
      case 'sent': return '#3b82f6';
      case 'approved': return '#10b981';
      case 'declined': return '#ef4444';
      case 'converted': return '#8b5cf6';
      case 'unapproved': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status || 'draft') {
      case 'draft': return <EditOutlined />;
      case 'sent': return <ClockCircleOutlined />;
      case 'approved': return <CheckCircleOutlined />;
      case 'declined': return <StopOutlined />;
      case 'converted': return <SwapOutlined />;
      case 'unapproved': return <ExclamationCircleOutlined />;
      default: return <ExclamationCircleOutlined />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status || 'draft') {
      case 'draft': return 'Draft';
      case 'sent': return 'Sent to Customer';
      case 'approved': return 'Approved by Customer';
      case 'declined': return 'Declined by Customer';
      case 'converted': return 'Converted to Work Order';
      case 'unapproved': return 'Unapproved';
      default: return status;
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      // Mock API call
      message.success(`Estimate status updated to ${newStatus}`);
      if (estimate) {
        setEstimate({ ...estimate, status: newStatus as any });
      }
    } catch (error) {
      message.error('Failed to update status');
    }
  };

  const handleConvertToWorkOrder = async () => {
    try {
      const values = await convertForm.validateFields();

      // An estimate the customer already booked does NOT need a second slot.
      //
      // This used to demand one unconditionally, so a job the customer had
      // scheduled themselves could not be converted from the UI at all — and if
      // staff picked a slot to get past it, the job ended up with TWO
      // appointments. `convertApprovedEstimateToWorkOrder` already re-links any
      // appointment attached to the estimate (estimateConversion.ts), so the
      // schedule the customer set carries over on its own.
      const alreadyBooked = !!existingAppointment;
      if (!alreadyBooked && !selectedAppointmentSlot) {
        message.error('Please select an appointment time slot');
        return;
      }

      // First, convert estimate to work order. Use the shared `api` axios
      // instance (baseURL .../admin) so we keep the 401 token-refresh interceptor
      // AND can forward the convert-form fields the backend reads from the body.
      const response = await api.post(`/estimates/${estimate?.id!}/convert`, {
        priority: values.priority,
        estimated_completion: values.estimated_completion,
        assigned_advisor: values.assigned_advisor,
      });
      const workOrder = response.data;
      const workOrderId = workOrder.id;

      // Create an appointment ONLY when there was not one already. The converter
      // re-links the existing one; creating another here would double-book the
      // technician against the same job.
      if (alreadyBooked) {
        message.success('Estimate converted to work order — the existing appointment carried over');
        setConvertModalVisible(false);
        convertForm.resetFields();
        setSelectedAppointmentSlot(null);
        fetchEstimate(estimate?.id!);
        return;
      }

      // Narrowing for the compiler; `alreadyBooked` returned above and the guard at
      // the top of this handler already refused a missing slot.
      const slot = selectedAppointmentSlot;
      if (!slot) return;

      try {
        const durationMinutes = values.duration_minutes || 120; // Default 2 hours
        const appointmentType = values.appointment_type || 'repair';

        await appointmentApi.create({
          work_order_id: workOrderId,
          estimate_id: estimate?.id,
          technician_id: slot.technician_id,
          customer_id: estimate?.customer_id!,
          vehicle_id: estimate?.vehicle_id!,
          appointment_type: appointmentType,
          title: `Work Order #${workOrder.wo_number || workOrderId}`,
          description: estimate?.internal_notes || estimate?.customer_notes || 'Work order from estimate conversion',
          start_time: slot.start_time,
          end_time: slot.end_time,
          duration_minutes: durationMinutes,
          travel_time_minutes: slot.travel_time_minutes || 0,
          buffer_time_minutes: 15,
          status: 'scheduled',
          manual_override: false,
          has_conflict: false,
          conflict_acknowledged: false,
          waiting_on_parts: false,
          customer_notified: false,
          customer_confirmed: false,
          reminder_sent: false
        });

        message.success('Estimate converted to work order and appointment scheduled successfully');
      } catch (appointmentError) {
        console.error('Error creating appointment:', appointmentError);
        message.warning('Work order created but failed to schedule appointment. Please schedule manually.');
      }

      setConvertModalVisible(false);
      convertForm.resetFields();
      setSelectedAppointmentSlot(null);
      fetchEstimate(estimate?.id!);
    } catch (error: any) {
      console.error('Error converting estimate:', error);
      if (error.errorFields) {
        message.error('Please fill in all required fields');
      } else {
        message.error(error?.response?.data?.error || 'Failed to convert estimate');
      }
    }
  };

  const handleScheduleAppointment = () => {
    // Check if appointment already exists
    if (existingAppointment) {
      setRescheduleModalVisible(true);
      return;
    }

    // Always use smart booking - unified system for all estimates
    setSmartBookingModalVisible(true);
  };

  const handleAppointmentModalClose = () => {
    setAppointmentModalVisible(false);
    fetchEstimate(estimate?.id!);
    fetchExistingAppointment(estimate?.id!);
  };

  const handleSmartBookingClose = () => {
    setSmartBookingModalVisible(false);
    fetchEstimate(estimate?.id!);
    fetchExistingAppointment(estimate?.id!);
  };

  const handleReschedule = async () => {
    if (!rescheduleReason.trim()) {
      message.error('Please provide a reason for rescheduling');
      return;
    }

    try {
      // Cancel existing appointment with reason using the proper cancel endpoint
      await appointmentApi.cancel(
        existingAppointment.id!,
        user?.id ?? 0,
        `Rescheduling: ${rescheduleReason}`
      );

      // Clear state
      setRescheduleModalVisible(false);
      setRescheduleReason('');
      setExistingAppointment(null);

      message.success('Appointment cancelled. Please schedule a new appointment.');

      // Small delay to ensure backend has processed the cancellation
      setTimeout(() => {
        // Open smart booking modal
        setSmartBookingModalVisible(true);
      }, 500);
    } catch (error: any) {
      console.error('Error rescheduling appointment:', error);
      message.error('Failed to reschedule appointment');
    }
  };

  const handleDelete = async () => {
    if (!estimate) return;

    try {
      await estimateApi.delete(estimate.id!);
      message.success('Estimate deleted successfully');
      navigate(`${routePrefix}/estimates`);
    } catch (error: any) {
      console.error('Error deleting estimate:', error);
      console.error('Error response:', error?.response?.data);
      const errorMessage = error?.response?.data?.error || 'Failed to delete estimate';
      message.error(errorMessage);
    }
  };

  const handlePrint = () => {
    setPdfConfigModalVisible(true);
  };

  if (loading || !estimate) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Text>Loading estimate details...</Text>
      </div>
    );
  }

  const canEdit = estimate.status !== 'converted';
  const canConvert = estimate.status === 'approved';
  const canSchedule = estimate.status !== 'converted';

  return (
    <div style={{ width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div className="estimate-detail-header">
        <Space align="center" className="estimate-detail-title-section">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`${routePrefix}/estimates`)}
          >
            Back to Estimates
          </Button>
          <Divider type="vertical" />
          <Title level={2} className="estimate-detail-title">
            {estimate.estimate_number}
          </Title>
          <Tag
            icon={getStatusIcon(estimate.status)}
            color={getStatusColor(estimate.status)}
            style={{
              fontSize: '16px',
              padding: '8px 20px',
              fontWeight: '600',
              borderRadius: '8px',
              border: `2px solid ${getStatusColor(estimate.status)}`,
              boxShadow: `0 2px 8px ${getStatusColor(estimate.status)}33`
            }}
          >
            {getStatusText(estimate.status).toUpperCase()}
          </Tag>
        </Space>
      </div>

      {/* The approval review, inline. Converting to a work order 409s while a
          submission is unreviewed (estimates.ts /convert), so the decision belongs
          on the screen where Convert lives — not on a separate queue page. Renders
          nothing when there is nothing waiting. */}
      <EstimateApprovalReview
        estimateId={estimateIdNum}
        onReviewed={() => { if (estimate.id) fetchEstimate(estimate.id); }}
      />

      {/* Customer + Vehicle context strip — scannable while walking a customer through the quote */}
      <Card size="small" className="estimate-context-bar" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 20px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <UserOutlined style={{ color: '#3b82f6' }} />
            <Text strong>
              {estimate.customer?.company_name ||
                `${estimate.customer?.first_name ?? ''} ${estimate.customer?.last_name ?? ''}`.trim() ||
                'Customer'}
            </Text>
          </span>
          {estimate.customer?.phone && (
            <Text type="secondary"><a href={`tel:${estimate.customer.phone}`}>{estimate.customer.phone}</a></Text>
          )}
          {estimate.customer?.email && (
            <Text type="secondary">{estimate.customer.email}</Text>
          )}
          <Divider type="vertical" style={{ height: 20 }} />
          {/* wrap + nowrap label: the clip controls below can grow to several
              buttons (failure state), and without this they squeezed the vehicle
              name into a one-character-wide column at 390px. */}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <CarOutlined style={{ color: '#f59e0b' }} />
            <Text strong style={{ whiteSpace: 'nowrap' }}>{assetLabel(estimate.vehicle)}</Text>
            {/* Desktop web-clip: one trigger per vehicle field. Each carries a short
                label because three bare paperclips are indistinguishable. These render
                nothing on mobile (no extension runtime) — the Capture button below is
                the path there. */}
            <ClipButton
              field="year"
              compactLabel="Yr"
              recordType="estimate"
              recordId={estimateIdNum}
              clip={clip}
              isAllowed={isOwnerOrAdvisor}
              onManualEntry={() => setVehicleCaptureOpen(true)}
              onFilled={() => { if (estimate.id) fetchEstimate(estimate.id); }}
            />
            <ClipButton
              field="make"
              compactLabel="Make"
              recordType="estimate"
              recordId={estimateIdNum}
              clip={clip}
              isAllowed={isOwnerOrAdvisor}
              onManualEntry={() => setVehicleCaptureOpen(true)}
              onFilled={() => { if (estimate.id) fetchEstimate(estimate.id); }}
            />
            <ClipButton
              field="model"
              compactLabel="Model"
              recordType="estimate"
              recordId={estimateIdNum}
              clip={clip}
              isAllowed={isOwnerOrAdvisor}
              onManualEntry={() => setVehicleCaptureOpen(true)}
              onFilled={() => { if (estimate.id) fetchEstimate(estimate.id); }}
            />
            {/* Works on every viewport and needs no extension — the only vehicle-capture
                path a phone has. Same REACT_APP_WEB_CLIP gate as the clip buttons. */}
            {clip.enabled && isOwnerOrAdvisor && (
              <Tooltip title="Paste or type year, make, model or VIN">
                <Button
                  size="small"
                  icon={<ScanOutlined />}
                  onClick={() => setVehicleCaptureOpen(true)}
                  data-testid="vehicle-capture-open"
                >
                  Capture
                </Button>
              </Tooltip>
            )}
          </span>
          {(estimate.vehicle?.current_mileage ?? 0) > 0 && (
            <Text type="secondary">{(estimate.vehicle?.current_mileage || 0).toLocaleString()} mi</Text>
          )}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>VIN</Text>
            <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              {estimate.vehicle?.vin || <Text type="secondary" italic>Not set</Text>}
            </Text>
            <ClipButton
              field="vin"
              recordType="estimate"
              recordId={estimateIdNum}
              clip={clip}
              isAllowed={isOwnerOrAdvisor}
              onManualEntry={() => setVehicleCaptureOpen(true)}
              onFilled={() => { if (estimate.id) fetchEstimate(estimate.id); }}
            />
          </span>
          {estimate.vehicle?.license_plate && (
            <Text type="secondary">Plate {estimate.vehicle.license_plate}</Text>
          )}
        </div>
      </Card>

      {/* Status Alert */}
      {estimate.status === 'converted' && (
        <Alert
          message="This estimate has been converted to a work order"
          description="No further changes can be made to this estimate."
          type="info"
          icon={<SwapOutlined />}
          style={{ marginBottom: '24px' }}
          showIcon
        />
      )}

      <Row gutter={[24, 24]}>
        {/* MAIN — the walk-through content */}
        <Col xs={24} lg={16}>
          {/* Line items — same read-only card view as the editor, with per-line
              discount stack and owner-gated margin popover */}
          <div className="estimate-view-items">
            <ServiceItemsTable
              items={estimate.line_items || []}
              onChange={() => {}}
              disabled
              taxRate={estimate.tax_rate}
              vehicle={estimate.vehicle}
            />
          </div>

          {/* Start an ALLDATA lookup, and watch the queue.
              Until this existed there was no way to run one from inside the app at
              all — every quote reviewed had been produced by a script. The queue is
              shown because there is ONE ALLDATA seat: a lookup takes minutes, so
              "nothing is happening" and "you are third in line" are different facts
              and staff have to be able to tell them apart. */}
          {estimate.id && (
            <Card size="small" title="ALLDATA" style={{ marginTop: 12 }}>
              {/* NO lookup button here, deliberately.
                  A lookup ADDS a line, and this screen is read-only — starting one
                  from a page that cannot show you the line it is about to write is
                  the wrong place for it. The button lives on the editor. The QUEUE
                  stays, because a job started from the editor runs for minutes and
                  the advisor may well be sitting on this screen when it lands. */}
              <LookupQueuePanel
                key={queueSeq}
                bare
                documentType="estimate"
                documentId={estimate.id}
                // A finished lookup has written a line, so the screen it was started
                // from is now stale.
                onCompleted={() => estimate.id && fetchEstimate(estimate.id)}
              />
            </Card>
          )}

          {/* What has to happen now, what can wait, and where doing two jobs on
              one visit saves a second alignment or recharge. Suggestions only —
              an advisor's own setting on the line always wins. */}
          {estimate.id && (estimate.line_items?.length ?? 0) > 1 && (
            <RepairPriorityCard
              estimateId={estimate.id}
              lines={(estimate.line_items || []).map((l: any) => ({
                id: l.id,
                description: l.description,
              }))}
            />
          )}

          {/* The shop's own briefing, in its own space rather than buried in a
              row expander. One collapsible block per line that has one, so a long
              procedure does not push the totals off the screen.

              Never shown to the customer: `shop_notes` is stripped from every
              public payload (migration 199). */}
          {(estimate.line_items || []).some((li: any) => li.shop_notes) && (
            <Card
              title="Shop Notes — tools, parts and procedure"
              extra={<Tag color="orange">Not shown to the customer</Tag>}
              style={{ marginBottom: 24 }}
            >
              <Collapse
                items={(estimate.line_items || [])
                  .filter((li: any) => li.shop_notes)
                  .map((li: any, i: number) => ({
                    key: String(li.id ?? i),
                    label: li.description,
                    children: <ShopNotesPanel value={li.shop_notes} bare />,
                  }))}
              />
            </Card>
          )}

          {/* Totals summary — prominent, server-authoritative amounts */}
          <Card className="estimate-totals-card" style={{ marginBottom: 24 }}>
            {(() => {
              const totalDiscounts = estimate.line_items.reduce((sum, item) => {
                let itemDiscounts = 0;
                if (item.labor_hours && item.hourly_rate) {
                  const baseLaborCost = item.labor_hours * item.hourly_rate;
                  const laborDiscountAmount = item.labor_discount_amount ||
                    (baseLaborCost * (item.labor_discount_percentage || 0) / 100);
                  itemDiscounts += laborDiscountAmount;
                }
                if (item.parts) {
                  itemDiscounts += item.parts.reduce((partSum, part) => {
                    const basePartCost = part.quantity * part.unit_price;
                    const partDiscountAmount = part.discount_amount ||
                      (basePartCost * (part.discount_percentage || 0) / 100);
                    return partSum + partDiscountAmount;
                  }, 0);
                }
                return sum + itemDiscounts;
              }, 0);
              const originalSubtotal = (estimate.subtotal || 0) + totalDiscounts;
              // Fees are folded into the stored tax/total but were never shown here,
              // so the visible rows didn't sum to Total. Back them out of the stored
              // fields (subtotal − discount + fees + tax = total) and show a Fees row.
              const feesTotal = feesFromStored({
                subtotal: estimate.subtotal,
                discount_amount: (estimate as any).discount_amount,
                tax_amount: estimate.tax_amount,
                total_amount: estimate.total_amount,
              });
              return (
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {totalDiscounts > 0 && (
                    <>
                      <div className="totals-row">
                        <Text type="secondary">Original Subtotal</Text>
                        <Text type="secondary">${originalSubtotal.toFixed(2)}</Text>
                      </div>
                      <div className="totals-row">
                        <Text type="secondary">Total Discounts</Text>
                        <Text style={{ color: '#ef4444' }}>-${totalDiscounts.toFixed(2)}</Text>
                      </div>
                    </>
                  )}
                  {canApplyDiscounts && estimate.status !== 'converted' && (
                    estimate.promo_code_id != null ? (
                      <div className="totals-row">
                        <Text type="secondary">Promo</Text>
                        <span>
                          <Tag color="green" style={{ margin: 0 }}>Applied</Tag>
                          <Button size="small" type="link" danger loading={promoBusy} onClick={handleRemovePromo} style={{ padding: 0, marginLeft: 8 }}>
                            Remove
                          </Button>
                        </span>
                      </div>
                    ) : (
                      <div className="totals-row">
                        <Text type="secondary">Promo code</Text>
                        <span style={{ display: 'inline-flex', gap: 8 }}>
                          <Input
                            size="small"
                            placeholder="Code"
                            value={promoInput}
                            onChange={(e) => setPromoInput(e.target.value)}
                            onPressEnter={() => handleApplyPromo(promoInput.trim())}
                            disabled={promoBusy}
                            style={{ width: 120 }}
                          />
                          <Button size="small" type="primary" loading={promoBusy} disabled={!promoInput.trim()} onClick={() => handleApplyPromo(promoInput.trim())}>
                            Apply
                          </Button>
                        </span>
                      </div>
                    )
                  )}
                  <div className="totals-row">
                    <Text>Subtotal</Text>
                    <Text strong>${(estimate.subtotal || 0).toFixed(2)}</Text>
                  </div>
                  {((estimate as any).discount_amount || 0) > 0 && (
                    <div className="totals-row">
                      <Text>Discount{(estimate as any).discount_type === 'percentage' && (estimate as any).discount_value ? ` (${(estimate as any).discount_value}%)` : ''}</Text>
                      <Text strong style={{ color: '#ef4444' }}>-${((estimate as any).discount_amount || 0).toFixed(2)}</Text>
                    </div>
                  )}
                  {feesTotal > 0 && (
                    <div className="totals-row">
                      <Text>Fees</Text>
                      <Text strong>${feesTotal.toFixed(2)}</Text>
                    </div>
                  )}
                  <div className="totals-row">
                    <Text>Tax ({estimate.tax_rate || 0}%)</Text>
                    <Text strong>${(estimate.tax_amount || 0).toFixed(2)}</Text>
                  </div>
                  {(estimate.diagnosis_credit_cents ?? 0) > 0 && (
                    <div className="totals-row">
                      <Text>Diagnosis credit</Text>
                      <Text strong style={{ color: '#22c55e' }}>
                        −${((estimate.diagnosis_credit_cents ?? 0) / 100).toFixed(2)}
                      </Text>
                    </div>
                  )}
                  <Divider style={{ margin: '8px 0' }} />
                  <div className="totals-row">
                    <Text strong style={{ fontSize: 20 }}>Total</Text>
                    <Text strong style={{ fontSize: 22, color: '#10b981' }}>
                      ${(estimate.total_amount || 0).toFixed(2)}
                    </Text>
                  </div>
                  {totalDiscounts > 0 && (
                    <div style={{ textAlign: 'right', fontSize: 12, color: '#ef4444' }}>
                      You saved ${totalDiscounts.toFixed(2)}
                    </div>
                  )}
                  {/* What the customer has already paid.
                      The portal offers them a deposit ("Pay 50% deposit —
                      $140.44") and takes the card, but this screen showed
                      nothing: an advisor looking at the estimate could not tell
                      the money had arrived, and the balance they would quote was
                      the full total. */}
                  {(Number((estimate as any).amount_paid) || 0) > 0 && (
                    <>
                      <div className="totals-row">
                        <Text>Paid</Text>
                        <Text strong style={{ color: '#22c55e' }}>
                          −${(Number((estimate as any).amount_paid) || 0).toFixed(2)}
                        </Text>
                      </div>
                      <div className="totals-row">
                        <Text strong>Balance due</Text>
                        <Text strong style={{ fontSize: 18 }}>
                          ${Math.max(
                            0,
                            (estimate.total_amount || 0) - (Number((estimate as any).amount_paid) || 0),
                          ).toFixed(2)}
                        </Text>
                      </div>
                    </>
                  )}
                </Space>
              );
            })()}
          </Card>

          {/* Customer notes */}
          {estimate.customer_notes && (
            <Card
              title={
                <Space size={4}>
                  <span>Customer Notes</span>
                  <ClipButton
                    field="customer_notes"
                    recordType="estimate"
                    recordId={estimateIdNum}
                    clip={clip}
                    isAllowed={isOwnerOrAdvisor}
                    onFilled={(val) => message.success(`Customer notes clipped: "${val}"`)}
                  />
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              <Text>{estimate.customer_notes}</Text>
            </Card>
          )}

          {/* Inspection status — read-only; hidden entirely on fetch error */}
          {dviInspection !== undefined && (
            <div style={{ marginBottom: 24 }}>
              <InspectionStatusCard inspection={dviInspection} />
            </div>
          )}

          {/* Meta */}
          <Card size="small" style={{ marginBottom: 24 }}>
            <Space split={<Divider type="vertical" />} wrap>
              <Text type="secondary">Created {dayjs(estimate.created_at).format('MMM DD, YYYY')}</Text>
              <Text type="secondary">
                Valid until {estimate.valid_until ? dayjs(estimate.valid_until).format('MMM DD, YYYY') : '—'}
              </Text>
            </Space>
          </Card>
        </Col>

        {/* RAIL — actions + history */}
        <Col xs={24} lg={8}>
          {/* Actions — Send is the dominant action; share is built into SendDialog.
              Shown on every viewport so mobile has full parity (schedule/print/edit/
              convert/copy/delete); the mobile sticky bar below is just a quick-Send. */}
          {(
            <Card className="estimate-actions-card" style={{ marginBottom: 24 }}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div className="estimate-send-primary">
                  <SendDialog
                    entityType="estimates"
                    entityId={estimate.id}
                    vehicleHasVin={!!estimate.vehicle?.vin}
                    vehicleHasMileage={!!estimate.vehicle?.current_mileage}
                    // G2: the policy decides what still has to be collected, not the
                    // hardcoded VIN/mileage pair. Records are already loaded here.
                    customerId={estimate.customer?.id ?? estimate.customer_id ?? null}
                    customer={estimate.customer ?? null}
                    vehicle={estimate.vehicle ?? null}
                    shareUrl={estimate.public_token ? `${window.location.origin}/estimate/${estimate.public_token}` : undefined}
                    onSent={() => fetchEstimate(estimate.id!)}
                  />
                </div>
                <Space wrap>
                  {canEdit && <Button icon={<EditOutlined />} onClick={() => navigate('edit')}>Edit</Button>}
                  {canConvert && <Button type="primary" icon={<SwapOutlined />} onClick={() => setConvertModalVisible(true)}>Convert</Button>}
                  {canSchedule && <Button icon={<CalendarOutlined />} onClick={handleScheduleAppointment}>{existingAppointment ? 'Reschedule' : 'Schedule'}</Button>}
                  <Button icon={<PrinterOutlined />} onClick={handlePrint}>Print / PDF</Button>
                  <PartsCatalogButton vehicle={estimate.vehicle} showText={true} />
                </Space>
                {/* Parts ETA (migration 162): mirrors the WO field — the booking
                    slot picker floors offered start times at this date so no
                    time is offered before the parts are expected. */}
                {canSchedule && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#888' }}>Parts ETA</span>
                    <DatePicker
                      size="small"
                      style={{ width: 150 }}
                      value={estimate.parts_eta_date ? dayjs(estimate.parts_eta_date) : null}
                      onChange={async (picked) => {
                        const next = picked ? picked.format('YYYY-MM-DD') : null;
                        const previous = estimate.parts_eta_date;
                        setEstimate({ ...estimate, parts_eta_date: next });
                        try {
                          await estimateApi.update(estimate.id!, { parts_eta_date: next });
                        } catch (error) {
                          console.error('Error updating parts ETA:', error);
                          setEstimate({ ...estimate, parts_eta_date: previous });
                          message.error('Failed to update parts ETA');
                        }
                      }}
                    />
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Switch
                      size="small"
                      checked={estimate.public_link_active !== false}
                      onChange={async (checked) => {
                        const previous = estimate.public_link_active;
                        setEstimate({ ...estimate, public_link_active: checked });
                        try {
                          await estimateApi.togglePublicLink(estimate.id!, checked);
                        } catch (error) {
                          console.error('Error toggling public link:', error);
                          setEstimate({ ...estimate, public_link_active: previous });
                          message.error('Failed to update customer access');
                        }
                      }}
                    />
                    <span style={{ fontSize: 12, color: '#666' }}>
                      Customer Access {estimate.public_link_active !== false ? 'ON' : 'OFF'}
                    </span>
                  </span>
                  <Popconfirm title="Delete this estimate?" onConfirm={handleDelete} okText="Yes" cancelText="No">
                    <Button danger size="small" icon={<DeleteOutlined />}>Delete</Button>
                  </Popconfirm>
                </div>
              </Space>
            </Card>
          )}

          {/* Activity log — collapsible */}
          <Collapse
            defaultActiveKey={isMobileView ? [] : ['log']}
            style={{ marginBottom: 24 }}
            items={[
              {
                key: 'log',
                label: 'Activity Log',
                children: <ActivityLogComponent entityType="estimate" entityId={estimate.id!} height="360px" />,
              },
            ]}
          />

          {/* Appointment */}
          {existingAppointment && (
            <Card
              title="Scheduled Appointment"
              style={{ marginBottom: 24 }}
              extra={<Avatar size="small" style={{ backgroundColor: '#52c41a' }} icon={<CalendarOutlined />} />}
            >
              <Alert
                message="Appointment Scheduled"
                description={
                  <div>
                    <div style={{ marginTop: 8 }}>
                      <Text strong>{dayjs(existingAppointment.start_time).format('MMMM D, YYYY')}</Text>
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <ClockCircleOutlined style={{ marginRight: 8 }} />
                      <Text>{dayjs(existingAppointment.start_time).format('h:mm A')} - {dayjs(existingAppointment.end_time).format('h:mm A')}</Text>
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <UserOutlined style={{ marginRight: 8 }} />
                      <Text>
                        {users.find(u => u.id === existingAppointment.technician_id)?.first_name}{' '}
                        {users.find(u => u.id === existingAppointment.technician_id)?.last_name}
                      </Text>
                    </div>
                  </div>
                }
                type="success"
                showIcon
              />
            </Card>
          )}

          {/* SMS */}
          {estimate.customer?.phone && (
            <SMSConversationThread
              estimateId={estimate.id}
              customerId={estimate.customer.id}
              customerPhone={estimate.customer.phone}
              height={400}
            />
          )}
        </Col>
      </Row>

      {/* Mobile sticky action bar — Send always reachable while scrolling */}
      {isMobileView && (
        <div className="estimate-mobile-actionbar">
          <div className="estimate-send-primary" style={{ flex: 1 }}>
            <SendDialog
              entityType="estimates"
              entityId={estimate.id}
              vehicleHasVin={!!estimate.vehicle?.vin}
              vehicleHasMileage={!!estimate.vehicle?.current_mileage}
              // G2: the policy decides what still has to be collected, not the
              // hardcoded VIN/mileage pair. Records are already loaded here.
              customerId={estimate.customer?.id ?? estimate.customer_id ?? null}
              customer={estimate.customer ?? null}
              vehicle={estimate.vehicle ?? null}
              shareUrl={estimate.public_token ? `${window.location.origin}/estimate/${estimate.public_token}` : undefined}
              onSent={() => fetchEstimate(estimate.id!)}
            />
          </div>
          {canEdit && (
            <Button icon={<EditOutlined />} onClick={() => navigate('edit')}>Edit</Button>
          )}
        </div>
      )}

      {/* Disclaimer Section */}
      {estimate.disclaimer && (
        <Card title="Important Terms & Conditions" style={{ marginTop: '24px' }}>
          <Alert
            message="Please Read Carefully"
            description={estimate.disclaimer}
            type="warning"
            showIcon
            style={{
              border: '2px solid #d97706',
              backgroundColor: '#fef3c7',
              color: '#92400e',
              fontSize: '14px',
              lineHeight: '1.6'
            }}
          />
        </Card>
      )}

      {/* Convert to Work Order Modal */}
      <Modal
        title="Convert to Work Order & Schedule Appointment"
        open={convertModalVisible}
        onOk={handleConvertToWorkOrder}
        onCancel={() => {
          setConvertModalVisible(false);
          convertForm.resetFields();
          setSelectedAppointmentSlot(null);
        }}
        okText={existingAppointment ? 'Convert to Work Order' : 'Convert & Schedule'}
        okButtonProps={{ type: 'primary', disabled: !existingAppointment && !selectedAppointmentSlot }}
        centered
        width={window.innerWidth < 500 ? '95vw' : 900}
      >
        <div style={{ marginBottom: '16px' }}>
          <Alert
            message="Convert Estimate to Work Order"
            description={
              existingAppointment
                ? `This will create a work order with all the services from this estimate. ${dayjs(existingAppointment.start_time).format('MMM D [at] h:mm A')} is already booked for this job and will carry over — you do not need to pick a time.`
                : 'This will create a new work order with all the services from this estimate and schedule an appointment. Both appointment selection and work order details are required.'
            }
            type="info"
            showIcon
          />
        </div>
        <Form form={convertForm} layout="vertical" initialValues={{ priority: 'normal', appointment_type: 'repair', duration_minutes: 120 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Priority" name="priority">
                <Select>
                  <Select.Option value="low">Low</Select.Option>
                  <Select.Option value="normal">Normal</Select.Option>
                  <Select.Option value="high">High</Select.Option>
                  <Select.Option value="urgent">Urgent</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Appointment Type" name="appointment_type">
                <Select>
                  <Select.Option value="diagnosis">Diagnosis</Select.Option>
                  <Select.Option value="repair">Repair</Select.Option>
                  <Select.Option value="inspection">Inspection</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Assign Advisor" name="assigned_advisor">
                <Select
                  placeholder="Select an advisor"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={users
                    .filter(user => user.role === 'owner' || user.role === 'advisor')
                    .map(user => ({
                      value: `${user.first_name} ${user.last_name}`,
                      label: `${user.first_name} ${user.last_name} (${user.role})`,
                    }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Estimated Completion" name="estimated_completion">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Estimated Duration (minutes)" name="duration_minutes">
            <Select>
              <Select.Option value={30}>30 minutes</Select.Option>
              <Select.Option value={60}>1 hour</Select.Option>
              <Select.Option value={90}>1.5 hours</Select.Option>
              <Select.Option value={120}>2 hours</Select.Option>
              <Select.Option value={180}>3 hours</Select.Option>
              <Select.Option value={240}>4 hours</Select.Option>
              <Select.Option value={360}>6 hours</Select.Option>
              <Select.Option value={480}>8 hours (Full day)</Select.Option>
            </Select>
          </Form.Item>

          <Divider>Select Appointment Time</Divider>

          <Form.Item>
            <AvailabilityPicker
              appointmentType={convertForm.getFieldValue('appointment_type') || 'repair'}
              customerId={estimate?.customer_id}
              vehicleId={estimate?.vehicle_id}
              estimatedDuration={convertForm.getFieldValue('duration_minutes') || 120}
              onSelectSlot={setSelectedAppointmentSlot}
              selectedSlot={selectedAppointmentSlot}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Old Enhanced Appointment Modal - REMOVED - Now using unified QuickBookingDrawer for all estimates */}
      {/* {estimate && (
        <EnhancedAppointmentModal
          visible={appointmentModalVisible}
          appointment={null}
          estimateId={estimate.id}
          onClose={handleAppointmentModalClose}
        />
      )} */}

      {/* PDF Configuration Modal */}
      {estimate && (
        <PDFConfigModal
          visible={pdfConfigModalVisible}
          estimate={estimate}
          onClose={() => setPdfConfigModalVisible(false)}
        />
      )}

      {/* Send Estimate Modal.
          UNREACHABLE from this screen: nothing calls setSendEstimateModalVisible(true)
          — the Send button above is <SendDialog>, and that is where the G2 checkpoint
          lives for the estimate screen. This block is still wired (and given the
          checkpoint props) so it behaves correctly if a caller ever opens it, and it
          IS opened from the owner dashboard's Estimates panel. Found 2026-07-30. */}
      {estimate && (
        <SendEstimateModal
          visible={sendEstimateModalVisible}
          onClose={() => setSendEstimateModalVisible(false)}
          estimateId={estimate.id!}
          estimateNumber={estimate.estimate_number}
          customerEmail={estimate.customer?.email}
          customerPhone={estimate.customer?.phone}
          // G2: the Send dialog shows what the customer will be asked for at
          // approval. The records are already loaded here, so the checkpoint
          // costs one policy call and no extra record fetch.
          customerId={estimate.customer?.id ?? estimate.customer_id ?? null}
          customer={estimate.customer ?? null}
          vehicle={estimate.vehicle ?? null}
          onSent={() => {
            setSendEstimateModalVisible(false);
            fetchEstimate(estimate.id!);
          }}
        />
      )}

      {/* Vehicle quick capture (WP-06) — paste-and-parse year/make/model/VIN.
          Same REACT_APP_WEB_CLIP gate as the clip buttons. */}
      {clip.enabled && isOwnerOrAdvisor && (
        <VehicleQuickCapture
          open={vehicleCaptureOpen}
          onClose={() => setVehicleCaptureOpen(false)}
          vehicleId={estimate.vehicle?.id ?? null}
          initial={{
            year: estimate.vehicle?.year ?? null,
            make: estimate.vehicle?.make ?? null,
            model: estimate.vehicle?.model ?? null,
            vin: estimate.vehicle?.vin ?? null,
          }}
          onSaved={() => { if (estimate.id) fetchEstimate(estimate.id); }}
        />
      )}

      {/* Appointment Booking - Unified SlotPicker drawer */}
      {estimate && (
        <QuickBookingDrawer
          visible={smartBookingModalVisible}
          estimateId={estimate.id!}
          onClose={handleSmartBookingClose}
          onBooked={handleSmartBookingClose}
        />
      )}

      {/* Reschedule Modal */}
      <Modal
        title="Reschedule Appointment"
        open={rescheduleModalVisible}
        onOk={handleReschedule}
        onCancel={() => {
          setRescheduleModalVisible(false);
          setRescheduleReason('');
        }}
        okText="Reschedule"
        okButtonProps={{ danger: true }}
      >
        <Alert
          message="Rescheduling Appointment"
          description="The current appointment will be cancelled. You will then be able to select a new time slot."
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        {existingAppointment && (
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <Text strong>Current Appointment:</Text>
            <div style={{ marginTop: '8px' }}>
              <Text>{dayjs(existingAppointment.start_time).format('MMMM D, YYYY [at] h:mm A')}</Text>
            </div>
          </div>
        )}
        <div>
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>Reason for Rescheduling *</Text>
          <TextArea
            rows={4}
            placeholder="Please provide a reason for rescheduling this appointment..."
            value={rescheduleReason}
            onChange={(e) => setRescheduleReason(e.target.value)}
            maxLength={500}
            showCount
          />
        </div>
      </Modal>
    </div>
  );
};

export default EstimateDetail;